import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

import type { AxiosRequestMetadata } from './axiosMetadata';

export type { AxiosRequestMetadata } from './axiosMetadata';

/** Axios request config extended with transport-layer metadata. */
export type AxiosConfigWithExtensions = InternalAxiosRequestConfig & {
  metadata?: AxiosRequestMetadata;
  /** Opt-in transport retry for non-idempotent requests (POST/PUT/PATCH/DELETE). */
  enableRetry?: boolean;
  /** When true, aborts a prior in-flight request with the same method, URL, and params. */
  cancelDuplicate?: boolean;
};

/** Public fetch options including transport retry opt-in. */
export type AxiosFetchRequestConfig = AxiosRequestConfig & {
  enableRetry?: boolean;
  cancelDuplicate?: boolean;
};

export function toAxiosConfigWithExtensions(
  config: AxiosFetchRequestConfig,
): AxiosConfigWithExtensions {
  return config as AxiosConfigWithExtensions;
}

export function asAxiosConfigWithExtensions(
  config: InternalAxiosRequestConfig,
): AxiosConfigWithExtensions {
  return config as AxiosConfigWithExtensions;
}

export function readRequestMetadata(
  config: InternalAxiosRequestConfig,
): AxiosRequestMetadata | undefined {
  return asAxiosConfigWithExtensions(config).metadata;
}

export function readEnableRetry(config: InternalAxiosRequestConfig): boolean {
  return asAxiosConfigWithExtensions(config).enableRetry === true;
}

export function readCancelDuplicate(
  config: InternalAxiosRequestConfig,
): boolean {
  return asAxiosConfigWithExtensions(config).cancelDuplicate === true;
}

export function withRequestMetadata(
  config: InternalAxiosRequestConfig,
): AxiosConfigWithExtensions {
  const current = asAxiosConfigWithExtensions(config);

  return {
    ...config,
    metadata: {
      startTime: Date.now(),
      retryCount: current.metadata?.retryCount ?? 0,
      inFlightKey: current.metadata?.inFlightKey,
    },
  };
}

export function withUpdatedRetryMetadata(
  config: InternalAxiosRequestConfig,
  retryCount: number,
): AxiosConfigWithExtensions {
  const current = asAxiosConfigWithExtensions(config);

  return {
    ...config,
    metadata: {
      startTime: current.metadata?.startTime ?? Date.now(),
      retryCount,
      inFlightKey: current.metadata?.inFlightKey,
    },
  };
}
